/**
 * @file 復習カードを 1 枚作る枠 (リーダーから開く)。
 *
 * @what: 問い・答えや確認のポイント (任意)・型やテーマ (任意)・次に復習する日 (既定は
 *   あす)・まなぶ人を受けて PUT する。出典 (資料・ページ・問) は呼び出し元が渡し、枠に
 *   名前で出す。まなぶ人の既定は記録シートと同じく最後に記録した人 (localStorage)。
 * @why: 読んでいる場所からその場で 1 枚作れると、「痕跡を記録に落とす」(§11) が web でも
 *   回る。問題そのものでなく型を主語に書けるよう、型の欄を置く。
 * @failure: 保存の失敗は入力を消さず 1 行で知らせる。学習者の読み込み失敗も手入力で作れる
 *   (まなぶ人が決まらなければ保存できない)。
 *
 * 長い文を書く2つの欄 (問い・答え) は React に値を持たせない
 * (hooks/useUncontrolledText.ts に理由がある)。保存に送る文はこの欄から取り、「作る」を
 * 押せるかの判定には問いが空でないかだけを state で持つ。
 */

import { useEffect, useRef, useState } from "react";
import { addDaysLocal, todayLocal } from "../api/dates";
import { memberLabel } from "../api/members";
import { newReviewCardId, putReviewCard, type StudyReviewCardRef } from "../api/reviewCards";
import { useLearners } from "../hooks/useLearners";
import { useUncontrolledText } from "../hooks/useUncontrolledText";
import { readLastLearner } from "../lib/lastLearner";
import { LearnerTabs } from "./LearnerTabs";
import { StudyDialog } from "./StudyDialog";
import "./study.css";

export function ReviewCardForm({
  familyId,
  cardRef,
  refLabel,
  defaultPrompt = "",
  onClose,
  onSaved,
}: {
  familyId: string;
  /** 出典 (資料・ページ・問)。React の ref と衝突するので cardRef。 */
  cardRef: StudyReviewCardRef;
  /** 出典の表示名 (「3級テキスト p.26」など)。 */
  refLabel: string;
  /** 問いの初期値 (問の設問など)。 */
  defaultPrompt?: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const learnersState = useLearners(familyId);
  const learners = learnersState.status === "ready" ? learnersState.learners : [];
  const [personId, setPersonId] = useState<string | null>(null);
  useEffect(() => {
    if (learnersState.status !== "ready" || personId !== null) return;
    const remembered = readLastLearner(familyId);
    const found = learnersState.learners.find((l) => l.person_id === remembered);
    const next = found ?? learnersState.learners[0];
    if (next) setPersonId(next.person_id);
  }, [learnersState, personId, familyId]);

  const prompt = useUncontrolledText(defaultPrompt);
  const answer = useUncontrolledText();
  // 「作る」を押せるかの判定にだけ使う。中身そのものは欄が持つ。
  const [promptFilled, setPromptFilled] = useState(defaultPrompt.trim() !== "");
  const [topic, setTopic] = useState("");
  const [dueOn, setDueOn] = useState(addDaysLocal(todayLocal(), 1));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const promptRef = prompt.textRef;
  const answerRef = answer.textRef;
  // defaultPrompt が変わったら欄も入れ直す (別の問から開き直したとき)。
  const seededFor = useRef(defaultPrompt);
  useEffect(() => {
    if (seededFor.current === defaultPrompt) return;
    seededFor.current = defaultPrompt;
    prompt.put(defaultPrompt);
    setPromptFilled(defaultPrompt.trim() !== "");
  }, [defaultPrompt, prompt]);

  const canSubmit =
    promptFilled && personId !== null && /^\d{4}-\d{2}-\d{2}$/.test(dueOn) && !saving;

  const submit = async () => {
    if (!personId || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await putReviewCard(familyId, newReviewCardId(), {
        learner_person_id: personId,
        prompt: promptRef.current.trim(),
        ...(answerRef.current.trim() ? { answer: answerRef.current.trim() } : {}),
        ...(topic.trim() ? { topic: topic.trim() } : {}),
        ref: cardRef,
        due_on: dueOn,
      });
      setSaved(true);
      onSaved?.();
    } catch (err: unknown) {
      console.error("[study] failed to create review card", err);
      setError("保存できませんでした。時間を置いて再度お試しください。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StudyDialog
      title="復習カードを作る"
      labelledBy="study-review-card-form-title"
      onClose={onClose}
    >
      <p className="study-dialog-sub">{refLabel}</p>
      {saved ? (
        <div className="study-reading-just">
          <p>復習カードを作りました。本だなの「きょうの復習」に、期日になると出ます。</p>
          <button type="button" className="study-primary-btn" onClick={onClose}>
            閉じる
          </button>
        </div>
      ) : (
        <>
          {learnersState.status === "error" ? (
            <p role="alert" className="study-error">
              まなぶ人の情報を読み込めませんでした。時間を置いて開き直してください。
            </p>
          ) : (
            <div className="study-learner-row">
              <span className="study-learner-row-label">まなぶ人</span>
              <LearnerTabs learners={learners} selectedId={personId} onSelect={setPersonId} />
            </div>
          )}
          <div className="study-log-form">
            <label className="study-log-field">
              <span>問い</span>
              <textarea
                ref={prompt.attach}
                rows={3}
                maxLength={500}
                placeholder="例: つるかめ算を面積図で解くとき、縦と横に何を置くか"
                onChange={(e) => {
                  promptRef.current = e.target.value;
                  setPromptFilled(e.target.value.trim() !== "");
                }}
              />
            </label>
            <label className="study-log-field">
              <span>答え・確認のポイント（任意）</span>
              <textarea
                ref={answer.attach}
                rows={2}
                maxLength={2000}
                placeholder="空なら、出典を開いて確かめます"
                onChange={(e) => {
                  answerRef.current = e.target.value;
                }}
              />
            </label>
            <div className="study-log-form-row">
              <label className="study-log-field">
                <span>型・テーマ（任意）</span>
                <input
                  type="text"
                  maxLength={120}
                  placeholder="例: つるかめ算の面積図"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                />
              </label>
              <label className="study-log-field">
                <span>次に復習する日</span>
                <input
                  type="date"
                  value={dueOn}
                  min={todayLocal()}
                  onChange={(e) => setDueOn(e.target.value)}
                />
              </label>
            </div>
            <div className="study-log-form-actions">
              <button
                type="button"
                className="study-primary-btn"
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {saving ? "保存しています..." : "作る"}
              </button>
              {error ? (
                <span role="alert" className="study-save-error">
                  {error}
                </span>
              ) : null}
            </div>
            {learners.find((l) => l.person_id === personId) ? (
              <p className="study-muted">
                {memberLabel(learners.find((l) => l.person_id === personId)!)}{" "}
                の復習カードになります。
              </p>
            ) : null}
          </div>
        </>
      )}
    </StudyDialog>
  );
}
